import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  setTokens, 
  getAccessToken, 
  getRefreshToken, 
  clearTokens, 
  isAuthenticated as checkIsAuthenticated,
  validateAccessToken 
} from '../services/authService.ts';

// User interface for type safety
export interface User {
  id: number;
  name: string;
  email?: string;
  avatar?: string;
}

// Auth context interface
interface AuthContextType {
  // Authentication state
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  
  // Authentication methods
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (email: string, password: string, name: string) => Promise<void>;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Mock user removed - using real authentication

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if user is authenticated using auth service
        const authenticated = checkIsAuthenticated();
        
        if (authenticated) {
          // Validate token and get user info
          const isValid = await validateAccessToken();
          if (isValid) {
            // Get user info using the API utility (which handles token refresh automatically)
            try {
              const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/me`, {
                headers: {
                  'Authorization': `Bearer ${getAccessToken()}`,
                },
                credentials: 'include',
              });

              if (response.ok) {
                const userData = await response.json();
                setUser({
                  id: userData.id,
                  name: userData.username,
                  email: userData.email,
                  avatar: `https://via.placeholder.com/150/6366f1/ffffff?text=${userData.username.charAt(0).toUpperCase()}`
                });
                setIsAuthenticated(true);
              } else {
                // Token validation failed, clear auth state
                clearTokens();
                setUser(null);
                setIsAuthenticated(false);
              }
            } catch (error) {
              console.error('Failed to get user info:', error);
              clearTokens();
              setUser(null);
              setIsAuthenticated(false);
            }
          } else {
            // Token validation failed, clear auth state
            clearTokens();
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          // No tokens stored, user must authenticate
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearTokens();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login method
  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }

      const tokenData = await response.json();
      const accessToken = tokenData.access_token;
      const refreshTokenValue = tokenData.refresh_token;

      // Store tokens using auth service
      setTokens(accessToken, refreshTokenValue);

      // Get user info
      const userResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!userResponse.ok) {
        throw new Error('Failed to get user info');
      }

      const userData = await userResponse.json();
      
      setUser({
        id: userData.id,
        name: userData.username,
        email: userData.email,
        avatar: `https://via.placeholder.com/150/6366f1/ffffff?text=${userData.username.charAt(0).toUpperCase()}`
      });
      setIsAuthenticated(true);
      localStorage.setItem('current_user', JSON.stringify(userData));
      
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Logout method
  const logout = async (): Promise<void> => {
    const storedRefreshToken = getRefreshToken();
    
    // Try to revoke refresh token on server
    if (storedRefreshToken) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify({ refresh_token: storedRefreshToken }),
          credentials: 'include',
        });
      } catch (error) {
        console.error('Logout error:', error);
        // Continue with local logout even if server call fails
      }
    }
    
    // Clear tokens and local state using auth service
    clearTokens();
    setUser(null);
    setIsAuthenticated(false);
  };

  // Register method
  const register = async (email: string, password: string, username: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, username }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      // Auto-login after registration
      try {
        await login(email, password);
      } catch (loginError) {
        console.error('Auto-login failed after registration:', loginError);
        // Don't throw error - user was created successfully, they can login manually
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Context value
  const value: AuthContextType = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    register,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Higher-order component for protected routes (for future use)
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isAuthenticated, isLoading } = useAuth();
    
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      );
    }
    
    if (!isAuthenticated) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Authentication Required</h2>
            <p className="text-gray-600">Please log in to access this page.</p>
          </div>
        </div>
      );
    }
    
    return <Component {...props} />;
  };
};
